import { supabase } from '../lib/supabase';

const PLAYER_PHOTOS_BUCKET = 'player-photos';

/**
 * Uploads a player avatar to storage
 */
export async function uploadPlayerAvatar(
  file: File,
  playerId: string,
  teamId: string
): Promise<string> {
  const fileExt = file.name.split('.').pop();
  const fileName = `${playerId}-${Date.now()}.${fileExt}`;
  const filePath = `player-avatars/${teamId}/${fileName}`;

  // Upload to Supabase Storage
  const { error: uploadError } = await supabase.storage
    .from(PLAYER_PHOTOS_BUCKET)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true,
    });

  if (uploadError) {
    // Log the full error for debugging
    console.error('[StorageUtils] Upload error details:', {
      message: uploadError.message,
      statusCode: uploadError.statusCode,
      error: uploadError.error,
      fullError: uploadError,
    });

    // Provide more helpful error messages based on error type
    if (uploadError.message?.includes('Bucket not found') || 
        uploadError.message?.includes('404') ||
        uploadError.statusCode === '404' ||
        uploadError.error === 'Bucket not found') {
      throw new Error(
        'Storage bucket "player-photos" not found. Please verify:\n' +
        '1. The bucket name is exactly "player-photos" (lowercase, with hyphen)\n' +
        '2. The bucket exists in your Supabase project\n' +
        '3. You are connected to the correct Supabase project\n' +
        '4. The bucket is set to Public\n' +
        '5. RLS policies are set up (see docs/STORAGE_BUCKET_SETUP.md)'
      );
    }
    
    if (uploadError.message?.includes('row-level security') || 
        uploadError.message?.includes('Unauthorized') ||
        uploadError.message?.includes('violates row-level security') ||
        uploadError.statusCode === '403' ||
        uploadError.error === 'Unauthorized') {
      throw new Error(
        'Permission denied. Please verify RLS policies are set up correctly:\n' +
        '1. Run the migration: supabase/migrations/20250201000001_setup_player_photos_storage.sql\n' +
        '2. Or manually run the SQL from docs/STORAGE_BUCKET_SETUP.md\n' +
        '3. Check that policies exist in Storage > Policies in Supabase Dashboard'
      );
    }
    
    // For other errors, include the original message
    throw new Error(
      uploadError.message || 
      uploadError.error || 
      `Failed to upload avatar: ${JSON.stringify(uploadError)}`
    );
  }

  // Get public URL
  const { data: { publicUrl } } = supabase.storage
    .from(PLAYER_PHOTOS_BUCKET)
    .getPublicUrl(filePath);

  return publicUrl;
}

